/**
 * Admin destinations and the tier each one requires.
 *
 * Multi-tenancy split "admin" into two independent tiers, and before this table existed the
 * split was written down in three unrelated places: the route guards in App.tsx, the shell
 * navigation, and the card list on the settings hub. They drifted — the navigation offered
 * every admin a link to /admin/settings after that route became platform-only, so a workspace
 * Owner's single admin affordance led to Access Denied.
 *
 * One list fixes that: whatever renders a link reads the same `tier` the route is guarded by,
 * so a link cannot be shown to someone the route will refuse.
 *
 * Route guards in App.tsx stay written out explicitly rather than looked up here. A route with
 * no guard prop is visibly unguarded when you read it; a route that looked its guard up would
 * fail open the moment a path fell out of this table.
 */

import {
  Bell,
  Building2,
  Globe,
  Lock,
  Mail,
  ScrollText,
  Users,
  type LucideIcon,
} from 'lucide-react';

/**
 * Which authority a destination needs.
 *
 *   tenantAdmin   — Owner or Admin of the active tenant. Client self-administration.
 *   platformAdmin — Akhile's back office. Never a client, however senior in their own tenant.
 */
export type AdminTier = 'tenantAdmin' | 'platformAdmin';

/** Sections on the settings hub. Order here is the order they render in. */
export type AdminGroup = 'access' | 'general' | 'notifications' | 'security' | 'platform';

export interface AdminDestination {
  path: string;
  label: string;
  description: string;
  icon: LucideIcon;
  tier: AdminTier;
  group: AdminGroup;
}

export interface AdminGroupMeta {
  id: AdminGroup;
  title: string;
  subtitle: string;
}

export const ADMIN_GROUPS: AdminGroupMeta[] = [
  {
    id: 'access',
    title: 'User Roles & Permissions',
    subtitle: 'Manage access control and role assignments',
  },
  { id: 'general', title: 'General Settings', subtitle: 'Application-wide configuration' },
  {
    id: 'notifications',
    title: 'Notifications',
    subtitle: 'Configure email and in-app notifications',
  },
  { id: 'security', title: 'Security', subtitle: 'Authentication and access control settings' },
  {
    id: 'platform',
    title: 'Platform Administration',
    subtitle: 'Akhile back office — spans every workspace',
  },
];

/**
 * Every admin destination. `tier` must match the guard on the matching route in App.tsx.
 */
export const ADMIN_DESTINATIONS: AdminDestination[] = [
  {
    path: '/admin/pending-registrations',
    label: 'Pending Registrations',
    description: 'Approve or reject new user registration requests',
    icon: Users,
    tier: 'tenantAdmin',
    group: 'access',
  },
  {
    path: '/admin/users',
    label: 'Users & Roles (Manage Access)',
    description:
      'Your workspace team and seats, plus per-user programme and project access — all in one place',
    icon: Users,
    tier: 'tenantAdmin',
    group: 'access',
  },
  {
    path: '/admin/system-roles',
    label: 'Roles Explained',
    description: 'What each platform and workspace role can do, before you grant one',
    icon: Users,
    tier: 'tenantAdmin',
    group: 'access',
  },
  {
    path: '/admin/project-roles',
    label: 'Access Audit',
    description:
      'Read-only overview of who has Editor/Viewer access to each project or programme (edit via Manage Access)',
    icon: Users,
    tier: 'tenantAdmin',
    group: 'access',
  },
  {
    path: '/admin/project-team',
    label: 'Team Functions',
    description:
      'Assign job functions (Financial Planner, Engineer, etc.) to users on projects — descriptive only, does not grant access',
    icon: Users,
    tier: 'tenantAdmin',
    group: 'access',
  },
  {
    path: '/admin/application-settings',
    label: 'Application Settings',
    description: 'Site name, timezone, language, and regional preferences',
    icon: Globe,
    tier: 'tenantAdmin',
    group: 'general',
  },
  {
    path: '/admin/notification-preferences',
    label: 'Notification Preferences',
    description: 'Default notification settings for new users and global notification rules',
    icon: Bell,
    tier: 'tenantAdmin',
    group: 'notifications',
  },
  {
    path: '/admin/email-configuration',
    label: 'Email Configuration',
    description: 'SMTP settings, email templates, and sender configuration',
    icon: Mail,
    tier: 'platformAdmin',
    group: 'notifications',
  },
  {
    path: '/admin/authentication',
    label: 'Authentication Settings',
    description: 'Password policies, session timeout, and two-factor authentication',
    icon: Lock,
    tier: 'platformAdmin',
    group: 'security',
  },
  {
    path: '/admin/tenants',
    label: 'Workspaces & Seats',
    description: 'Create client workspaces, set their purchased seats, and invite their first Owner',
    icon: Building2,
    tier: 'platformAdmin',
    group: 'platform',
  },
  {
    path: '/admin/audit-logs',
    label: 'Audit Logs',
    description: 'Security and administrative events across every workspace',
    icon: ScrollText,
    tier: 'platformAdmin',
    group: 'platform',
  },
];

/** What the viewer is allowed to be — the two flags AuthContext exposes. */
export interface AdminCapabilities {
  isTenantAdmin: boolean;
  isPlatformAdmin: boolean;
}

export const canReachAdmin = (
  destination: AdminDestination,
  { isTenantAdmin, isPlatformAdmin }: AdminCapabilities
): boolean => (destination.tier === 'platformAdmin' ? isPlatformAdmin : isTenantAdmin);

export const adminDestinationsFor = (caps: AdminCapabilities): AdminDestination[] =>
  ADMIN_DESTINATIONS.filter((d) => canReachAdmin(d, caps));

/**
 * Whether to offer an "Admin" affordance at all. Either tier has somewhere to go, so both see
 * it — what differs is which destinations are inside.
 */
export const hasAnyAdminAccess = ({ isTenantAdmin, isPlatformAdmin }: AdminCapabilities): boolean =>
  isTenantAdmin || isPlatformAdmin;
